import { createApi } from "@reduxjs/toolkit/query/react";
import { apiRequestBaseQuery } from "../../services/rtkBaseQuery";
import { getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";
import {
    CONTENT_MANAGEMENT_LEVELS,
    buildScopeData,
    scopedRequestType,
} from "../../services/contentManagementScope";

// =============================================================================
// ROYAL MOMENTUM - Store Visits API
// =============================================================================

// The ship/destination content endpoints are read at two scopes: one agency's own
// copy (the store visit and agency Content Management) and a whole market's copy
// (all-accounts Content Management). Params carrying a `countryCode` mean the
// latter; `contentManagementScope` decides the payload key and request type for
// both, so neither is spelled out here.
const levelForParams = (params) =>
    params?.countryCode ? CONTENT_MANAGEMENT_LEVELS.COUNTRY : CONTENT_MANAGEMENT_LEVELS.AGENCY;

/**
 * Cache key for the agency ship/destination reads.
 *
 * These endpoints are queried from two places that compute their args
 * independently: the Content Management preview/edit page, and the ship or
 * destination template it embeds. Both describe the same query, but they spell an
 * absent agency differently — the page passes `scope.agencyId`, which is
 * `undefined` when no brand is selected, while the templates end their fallback
 * chain on an explicit `null`.
 *
 * RTK Query's default serializer is `JSON.stringify`, which **drops** an
 * `undefined` value and **keeps** a `null` one. So at market level — the one scope
 * with no selected brand by design — the two became separate cache entries: saving
 * refetched the page's entry while the preview went on rendering its own, stale,
 * one. It only looked like a render bug because the network tab showed the refetch
 * that did happen, against the entry nobody was displaying.
 *
 * Normalising every field here makes the key describe the query rather than the
 * caller's spelling. The raw args still reach `query()` untouched, and they build
 * the same payload either way (`buildScopeData` ignores `agencyId` at market
 * level), so merging the entries cannot change what is requested.
 */
const serializeScopedReadArgs = ({ endpointName, queryArgs }) =>
    `${endpointName}(${JSON.stringify({
        agencyId: queryArgs?.agencyId ?? null,
        countryCode: queryArgs?.countryCode ?? null,
        shipUid: queryArgs?.shipUid ?? null,
        language: queryArgs?.language || "en",
    })})`;

const scopeDataForParams = (params) =>
    buildScopeData({
        level: levelForParams(params),
        agencyId: params?.agencyId,
        countryCode: params?.countryCode,
    });

export const royalMomentumStoreVisitsApi = createApi({
    reducerPath: "royalMomentumStoreVisitsApi",
    baseQuery: apiRequestBaseQuery,
    // "StoreVisitDetail" covers one visit's own saved answers (step data / progress / summary);
    // it is kept apart from "StoreVisits" so a step save inside the visit flow does not refetch
    // the whole upcoming/past visit lists.
    tagTypes: ["StoreVisitStats", "StoreVisits", "StoreVisitDetail", "Stores"],
    endpoints: (builder) => ({
        getTotalStoreVisits: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getTotalStoreVisitsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        visit_status: params.visitStatus ?? "",
                        type: params.type || "year",
                        date: params.date || new Date().getFullYear().toString(),
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getTotalPassengersYtd: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getTotalPassengersYtdV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        visit_status: params.visitStatus ?? "",
                        type: params.type || "year",
                        date: params.date || new Date().getFullYear().toString(),
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getTotalRevenueYtd: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getTotalRevenueYtdV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        visit_status: params.visitStatus ?? "",
                        type: params.type || "year",
                        date: params.date || new Date().getFullYear().toString(),
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getTotalStoresNotVisited: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getTotalStoresNotVisitedV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        type: params.type || "year",
                        date: params.date || new Date().getFullYear().toString(),
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getStoreVisitImpact: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getStoreVisitImpactV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        type: params.type || "year",
                        date: params.date || new Date().getFullYear().toString(),
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        // Version is chosen by whether the caller has an agency to send, not by `show`.
        //
        // GET_STORE_VISITS_REVENUE (V1) is retired — every caller should be on V2. V2's agency
        // resolver needs `agency_id` to scope the list; without one it falls back to the `[-1]`
        // sentinel and returns nothing. So a caller that cannot yet supply `travelAgencyUid` still
        // gets routed to V1 rather than firing a V2 request guaranteed to come back empty — that is
        // a stopgap for an unmigrated caller, not a supported permanent state. Every `show: "history"`
        // call site should be moved onto `travelAgencyUid` the same way the `show: "upcoming"` ones
        // already are; see V2_API_MIGRATION.md.
        getStoreVisitsRevenue: builder.query({
            query: (params = {}) => {
                const userDetails = getUserDetails();
                const show = params.show || "upcoming";
                const hasAgency = params.travelAgencyUid != null && params.travelAgencyUid !== "";
                return {
                    requestType: hasAgency
                        ? requestType.getStoreVisitsRevenueV2
                        : requestType.getStoreVisitsRevenue,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        // agency_id (= selectedBrand.uid = agency_id_no_r) — V2 only.
                        ...(hasAgency && { agency_id: params.travelAgencyUid }),
                        show,
                        page: params.page || 1,
                        limit: params.limit || 50,
                        ...(params.type && { type: params.type }),
                        ...(params.date && { date: params.date }),
                        ...(params.search && { search: params.search }),
                    },
                };
            },
            providesTags: ["StoreVisits"],
            // Booked visits are user-created records, so a stale list is always wrong. Dropping
            // the entry on unsubscribe makes every remount refetch, which covers the
            // book-a-visit → your-visits and complete-visit → past-visits paths.
            keepUnusedDataFor: 0,
        }),

        getStoreNotVisited: builder.query({
            query: (params = {}) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getStoreNotVisitedV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        page: params.page || 1,
                        limit: params.limit || 10,
                    },
                };
            },
            providesTags: ["StoreVisits"],
        }),

        getStores: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid.
                    // `with_contact` asks the BE to attach each store's contacts to its row.
                    requestType: requestType.getStoresV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        with_contact: true,
                    },
                };
            },
            providesTags: ["Stores"],
        }),

        addStoreVisit: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (the per-store agency_id from GET_STORES_V2) replaces
                    // store_user_uid, which those store rows no longer return.
                    requestType: requestType.addStoreVisitV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.agencyId,
                        visit_date: params.visitDate,
                        visit_time: params.visitTime,
                        visit_type: params.visitType || null,
                    },
                };
            },
            invalidatesTags: ["StoreVisits", "StoreVisitStats"],
        }),

        // Update Store Visit — the step-6 "complete visit" write (is_visited: 1) and any other
        // edit to a booked visit. Defined here rather than called as a raw apiRequest so that
        // finishing a visit moves it out of the upcoming lists and into the stats immediately.
        updateStoreVisit: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.updateStoreVisit,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        store_visit_id: params.storeVisitId,
                        ...(params.isVisited !== undefined && { is_visited: params.isVisited }),
                        ...(params.visitDate && { visit_date: params.visitDate }),
                        ...(params.visitTime && { visit_time: params.visitTime }),
                        ...(params.visitType && { visit_type: params.visitType }),
                    },
                };
            },
            invalidatesTags: ["StoreVisits", "StoreVisitStats", "StoreVisitDetail"],
        }),

        getTotalUniqueStoreVisits: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getTotalUniqueStoreVisitsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        visit_status: params.visitStatus ?? "",
                        type: params.type || "year",
                        date: params.date || new Date().getFullYear().toString(),
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getAverageVisitsPerStore: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getAverageVisitsPerStoreV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        visit_status: params.visitStatus ?? "",
                        type: params.type || "year",
                        date: params.date || new Date().getFullYear().toString(),
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getStoreWithoutAVisit: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getStoreWithoutAVisitV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        visit_status: params.visitStatus ?? "",
                        type: params.type || "year",
                        date: params.date || new Date().getFullYear().toString(),
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getTotalStoreVisitsByMonth: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getTotalStoreVisitsByMonthV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        year: params.year || new Date().getFullYear().toString(),
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getStoreVisitImpactOnBookings: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getStoreVisitImpactOnBookingsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        type: params.type || "year",
                        date: params.date || new Date().getFullYear().toString(),
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getCruiseBrandsSold: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getCruiseBrandsSoldV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getLandBasedBrandsSold: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getLandBasedBrandsSoldV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getOceanCruiseBusinessPercentage: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getOceanCruiseBusinessPercentageV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getInStorePromotionalOpportunities: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getInStorePromotionalOpportunitiesV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getStoreVisitMarketCondition: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getStoreVisitMarketConditionV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getStoreVisitProgressCompletion: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getStoreVisitProgressCompletionV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                    },
                };
            },
            providesTags: ["StoreVisitStats"],
        }),

        getStoreDetail: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // `agency_id` carries whichever scope the caller has. The Pre-Visit Briefing,
                    // StartVisit and StoreVisitLayout send `storeAgencyId` (the store being visited —
                    // the visit row's `agent_id`, NOT its own `id`), so their header describes the
                    // branch being visited. The three activation templates still send only
                    // `travelAgencyUid` (= selectedBrand.uid) and resolve the selected account, which
                    // is correct for them.
                    requestType: requestType.getStoreDetailV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId ?? params.travelAgencyUid,
                    },
                };
            },
            providesTags: (result, error, params) => [
                { type: "Stores", id: params.storeUserUid },
            ],
        }),

        // --- Pre-Visit Briefing store metrics — V2 --------------------------------------------------
        // The RoyalMomentumPreVisitBriefing cards, and its only consumer. They take `agency_id` and no
        // store key, and that `agency_id` is now the **store being visited** — `?storeAgencyId=` from
        // the URL, which `buildVisitQueryFromRow` sets to the visit row's `agent_id`. `storeVisitUid`
        // (a sibling param, the visit row's own `id`) is a different concept — "which booked visit is
        // this," used for `store_visit_id` on the progress/step-save endpoints — and does not feed
        // `agency_id` anywhere. See the header comment on `buildVisitQuery` in `utils/storeId.js`.
        //
        // They were previously scoped to the account selected in the brand selector
        // (`selectedBrand.uid`), which meant every branch of a chain showed identical figures — the
        // HQ total — on a page whose whole purpose is to brief a rep on one branch. Raised in QA.
        //
        // `storeVisitUid` is the only key these send, deliberately with no fallback to the account:
        // opening the page without it now yields empty cards rather than HQ numbers presented as
        // branch numbers. The component skips the requests in that case. See
        // RoyalMomentumPreVisitBriefing.md.
        getStoreRevenueThisYear: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreRevenueThisYearV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreRevenueSinceLastVisit: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreRevenueSinceLastVisitV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStorePaxThisYear: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStorePaxThisYearV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        // --- Store stats keyed by agency_id (GET_STORES_V2 store rows) -----------------------------
        // Book A Visit's copies of the same three V2 endpoints. Kept separate only because that page
        // names the key `agencyId` (it reads the per-store `agency_id` straight off a GET_STORES_V2
        // row) while the trio above names it `storeUserUid` (from the visit flow's `?store=` param).
        // Now that the trio above is V2 too, these are otherwise identical — worth collapsing into
        // one pair of endpoints with a single param name next time this file is touched.
        getStoreRevenueThisYearByAgency: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreRevenueThisYearV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.agencyId,
                    },
                };
            },
        }),

        getStorePaxThisYearByAgency: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStorePaxThisYearV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.agencyId,
                    },
                };
            },
        }),

        getStoreRevenueSinceLastVisitByAgency: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreRevenueSinceLastVisitV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.agencyId,
                    },
                };
            },
        }),

        getStoreWowBookings: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreWowBookingsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreGroupBookings: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getGroupBookingsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreClubRoyalsMembers: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getClubRoyalsMembersV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreMyRoyalGenieMembers: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getMyRoyalGenieMembersV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreProposalsGenerated: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreProposalsGeneratedV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreBookingsRedeemedUpperDeck: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreBookingsRedeemedUpperDeckV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreClubLearnModulesCompleted: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreClubLearnModulesCompletedV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreProductMix: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreProductMixV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreBookingsAreSailing: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreBookingsAreSailingV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getStoreShipMix: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreShipMixV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getStoreBookingToDeparture: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreBookingToDepartureV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                        year: params.year || new Date().getFullYear().toString(),
                    },
                };
            },
        }),

        getStoreBookingOverview: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreBookingOverviewV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                        page: params.page || 1,
                        limit: params.limit || 10,
                    },
                };
            },
        }),

        getStoreTopCruises: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreTopCruisesV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                        limit: params.limit || 10,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getStoreCustomersCabinTypes: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreCustomersCabinTypesV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getStoreTotalBookings: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreTotalBookingsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getStoreRevenueThisYearFiltered: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreRevenueThisYearV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getStoreTotalPassengers: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreTotalPassengersV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getStoreAgentsEnrolledUpperDeck: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreAgentsEnrolledUpperDeckV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreBookingsClaimedUpperDeck: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreBookingsClaimedUpperDeckV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreBookingsClaimedThroughUpperDeck: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreBookingsClaimedThroughUpperDeckV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreBookingsClaimViaUpperDeck: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreBookingsClaimViaUpperDeckV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreModulesCompleted: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreModulesCompletedV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreRoyalGurus: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreRoyalGurusV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreRoyalGurusList: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreRoyalGurusListV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                    },
                };
            },
        }),

        getStoreFocusModules: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreFocusModulesV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                        year: params.year || "2026",
                    },
                };
            },
        }),

        getStoreTotalProposalsCreated: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreTotalProposalsCreatedV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getStoreUniqueAgentsUsedGenie: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreUniqueAgentsUsedGenieV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getStoreCustomerYearlyTrips: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreCustomerYearlyTripsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeAgencyId,
                        year: params.year || new Date().getFullYear().toString(),
                    },
                };
            },
        }),

        getShipDetails: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getShipDetails,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        ship_uid: params.shipUid,
                        language: params.language || "en",
                    },
                };
            },
        }),
        /**
         * Ship details for one agency. Same response shape as `getShipDetails`
         * plus an `agency_id` (= selectedBrand.uid = agency_id_no_r), so callers
         * and every downstream `shipDataMap` consumer are unchanged.
         */
        // Scoped by agency, or by market when `countryCode` is given — the
        // all-accounts branch of Content Management edits a whole country's copy of
        // a ship page. `contentManagementScope` owns both payload keys.
        getAgencyShipDetails: builder.query({
            serializeQueryArgs: serializeScopedReadArgs,
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: scopedRequestType(
                        "getAgencyShipDetails",
                        levelForParams(params)
                    ),
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        ...scopeDataForParams(params),
                        ship_uid: params.shipUid,
                        language: params.language || "en",
                    },
                };
            },
        }),

        getDestinationDetails: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getDestinationDetails,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        data_key: params.dataKey,
                        language: params.language || "en",
                    },
                };
            },
        }),

        getTradeDestinations: builder.query({
            query: () => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getTradeDestinations,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                    },
                };
            },
        }),

        /**
         * Destinations for one agency — the replacement for `getTradeDestinations`.
         *
         * The records themselves are unchanged, but they arrive wrapped:
         * `data.destinations` rather than a bare `data` array, each carrying
         * `is_customised` / `custom_destination`. Use `destinationsFromResponse`
         * to read either shape.
         */
        // Scoped by agency, or by market when `countryCode` is given — see
        // `getAgencyShipDetails` above.
        getAgencyDestinations: builder.query({
            serializeQueryArgs: serializeScopedReadArgs,
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: scopedRequestType(
                        "getAgencyDestinations",
                        levelForParams(params)
                    ),
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        ...scopeDataForParams(params),
                        language: params.language || "en",
                    },
                };
            },
        }),

        getBrands: builder.query({
            // V2 is keyed by `market` — not by agency. V1's fallback chain, which resolved the
            // market from `user_uid`/country BE-side, is gone: a missing or blank value returns
            // ERROR_MISSING_MARKET_PARAMETER and an unrecognised one ERROR_INVALID_PARAMETER, so
            // the caller must pass one of the market codes in utilities/countries.js (UK, IE,
            // NORDICS, MT, AE, ES — case-insensitive). `user_uid` still rides along, but for auth
            // only. `type` stays optional: omit it to get cruise and land together.
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getBrandsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        market: params.market,
                        type: params.type,
                    },
                };
            },
        }),

        setStoreVisitStepData: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.setStoreVisitStepData,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        store_visit_id: params.storeVisitId,
                        step: params.step,
                        fields: params.fields,
                    },
                };
            },
            invalidatesTags: ["StoreVisitDetail"],
        }),

        setStoreVisitProgress: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.setStoreVisitProgress,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        store_visit_id: params.storeVisitId,
                        step: params.step,
                    },
                };
            },
            invalidatesTags: ["StoreVisitDetail"],
        }),

        getStoreVisitProgress: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreVisitProgress,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        store_visit_id: params.storeVisitId,
                    },
                };
            },
        }),

        // Reads back everything the visitor typed across steps 1–6, so it must never be served
        // from a cache that predates the last step save.
        getStoreVisitSummary: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getStoreVisitSummary,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        store_visit_id: params.storeVisitId,
                    },
                };
            },
            providesTags: ["StoreVisitDetail"],
            keepUnusedDataFor: 0,
        }),
    }),
});

export const {
    useGetTotalStoreVisitsQuery,
    useGetTotalPassengersYtdQuery,
    useGetTotalRevenueYtdQuery,
    useGetTotalStoresNotVisitedQuery,
    useGetStoreVisitImpactQuery,
    useGetStoreVisitsRevenueQuery,
    useGetStoreNotVisitedQuery,
    useGetStoresQuery,
    useAddStoreVisitMutation,
    useUpdateStoreVisitMutation,
    useGetTotalUniqueStoreVisitsQuery,
    useGetAverageVisitsPerStoreQuery,
    useGetStoreWithoutAVisitQuery,
    useGetTotalStoreVisitsByMonthQuery,
    useGetStoreVisitImpactOnBookingsQuery,
    useGetCruiseBrandsSoldQuery,
    useGetLandBasedBrandsSoldQuery,
    useGetOceanCruiseBusinessPercentageQuery,
    useGetInStorePromotionalOpportunitiesQuery,
    useGetStoreVisitMarketConditionQuery,
    useGetStoreVisitProgressCompletionQuery,
    useGetStoreDetailQuery,
    useGetStoreRevenueThisYearQuery,
    useGetStoreRevenueSinceLastVisitQuery,
    useGetStorePaxThisYearQuery,
    useGetStoreRevenueThisYearByAgencyQuery,
    useGetStorePaxThisYearByAgencyQuery,
    useGetStoreRevenueSinceLastVisitByAgencyQuery,
    useGetStoreWowBookingsQuery,
    useGetStoreGroupBookingsQuery,
    useGetStoreClubRoyalsMembersQuery,
    useGetStoreMyRoyalGenieMembersQuery,
    useGetStoreProposalsGeneratedQuery,
    useGetStoreBookingsRedeemedUpperDeckQuery,
    useGetStoreClubLearnModulesCompletedQuery,
    useGetStoreProductMixQuery,
    useGetStoreTotalBookingsQuery,
    useGetStoreRevenueThisYearFilteredQuery,
    useGetStoreTotalPassengersQuery,
    useGetStoreCustomerYearlyTripsQuery,
    useGetStoreBookingsAreSailingQuery,
    useGetStoreCustomersCabinTypesQuery,
    useGetStoreTopCruisesQuery,
    useGetStoreBookingOverviewQuery,
    useGetStoreBookingToDepartureQuery,
    useGetStoreShipMixQuery,
    useGetStoreTotalProposalsCreatedQuery,
    useGetStoreUniqueAgentsUsedGenieQuery,
    useGetStoreAgentsEnrolledUpperDeckQuery,
    useGetStoreBookingsClaimedUpperDeckQuery,
    useGetStoreBookingsClaimedThroughUpperDeckQuery,
    useGetStoreBookingsClaimViaUpperDeckQuery,
    useGetStoreModulesCompletedQuery,
    useGetStoreRoyalGurusQuery,
    useGetStoreRoyalGurusListQuery,
    useGetStoreFocusModulesQuery,
    useSetStoreVisitStepDataMutation,
    useSetStoreVisitProgressMutation,
    useGetStoreVisitProgressMutation,
    useGetStoreVisitSummaryQuery,
    useGetShipDetailsQuery,
    useGetAgencyShipDetailsQuery,
    useGetDestinationDetailsQuery,
    useGetTradeDestinationsQuery,
    useGetAgencyDestinationsQuery,
    useGetBrandsQuery,
} = royalMomentumStoreVisitsApi;
